import { QueryClient } from '@tanstack/react-query';
import type { SupportedBotName } from '@discord-bot-platform/service-client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 30 seconds
      staleTime: 30 * 1000,
      // Cache time: 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error: unknown) => {
        const status = typeof error === 'object' && error !== null && 'status' in error ? (error as { status?: number }).status : undefined;
        if (status === 404 || status === 401) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus
      refetchOnWindowFocus: true,
      // Don't refetch on reconnect by default
      refetchOnReconnect: 'always',
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Query keys factory for consistent key management
export const queryKeys = {
  all: ['api'] as const,
  
  // Orchestrator queries
    orchestrator: {
      all: () => [...queryKeys.all, 'orchestrator'] as const,
      health: () => [...queryKeys.orchestrator.all(), 'health'] as const,
      systemHealth: () => [...queryKeys.orchestrator.health(), 'system'] as const,
      serviceHealth: (service: string) => [...queryKeys.orchestrator.health(), 'service', service] as const,
      metrics: () => [...queryKeys.orchestrator.all(), 'metrics'] as const,
      allMetrics: () => [...queryKeys.orchestrator.metrics(), 'all'] as const,
      resourceMetrics: () => [...queryKeys.orchestrator.metrics(), 'resources'] as const,
      incidents: (params?: { status?: string; service?: string; limit?: number }) => [...queryKeys.orchestrator.all(), 'incidents', params] as const,
    },
  
  // Core services queries
  core: {
    all: () => [...queryKeys.all, 'core'] as const,
    llm: {
      all: () => [...queryKeys.core.all(), 'llm'] as const,
      providersHealth: () => [...queryKeys.core.llm.all(), 'providers-health'] as const,
      botConfig: (botName: SupportedBotName) => [...queryKeys.core.llm.all(), 'bot-config', botName] as const,
      botPrompts: (botName: SupportedBotName) => [...queryKeys.core.llm.all(), 'bot-prompts', botName] as const,
    },
    hotelBull: {
      all: () => [...queryKeys.core.all(), 'hotel-bull'] as const,
      profile: (userId: string, guildId: string) => 
        [...queryKeys.core.hotelBull.all(), 'profile', userId, guildId] as const,
      stats: (guildId?: string) => 
        [...queryKeys.core.hotelBull.all(), 'stats', guildId] as const,
      recentProfiles: (limit: number) => 
        [...queryKeys.core.hotelBull.all(), 'recent-profiles', limit] as const,
    },
    permissions: {
      all: () => [...queryKeys.core.all(), 'permissions'] as const,
      guild: (guildId: string) => [...queryKeys.core.permissions.all(), 'guild', guildId] as const,
    },
  },
};

export default queryClient;
